import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Group, Text } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { CreateProjectDtoStatusEnum, ProjectDtoPriorityEnum } from 'src/Services/API/generated/api';
import { PROJECT_LABEL_COLORS } from 'src/Utils/projectLabelColors';

const PRIORITY_CONFIG: Record<ProjectDtoPriorityEnum, { bg: string; text: string; label: string }> =
  {
    [ProjectDtoPriorityEnum.High]: { bg: '#FFE3E3', text: '#C92A2A', label: 'Haute' },
    [ProjectDtoPriorityEnum.Medium]: { bg: '#FFF3BF', text: '#E67700', label: 'Moyenne' },
    [ProjectDtoPriorityEnum.Low]: { bg: '#D3F9D8', text: '#2B8A3E', label: 'Basse' },
  };

import { LikeButton } from '../LikeButton';

import styles from './ProjectCard.module.scss';
import { Props } from './ProjectCard.types';
import { ProjectVoting } from './ProjectVoting';

const extractPlainText = (description: string | null | undefined): string | null => {
  if (!description) return null;
  try {
    const parsed = JSON.parse(description) as SlateNode[];
    if (!Array.isArray(parsed)) return description;

    const extractText = (nodes: SlateNode[]): string => {
      return nodes
        .map(node => {
          if ('text' in node) return node.text;
          if ('children' in node) return extractText(node.children);
          return '';
        })
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    return extractText(parsed) || null;
  } catch {
    return description;
  }
};

const ProjectCard = ({ project }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assetId } = useParams<{ assetId: string }>();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
    data: {
      type: 'Project',
      project,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const creator = project.createdBy;

  const projectLabel = project.label;
  const labelColor = projectLabel ? PROJECT_LABEL_COLORS[projectLabel] : null;

  const coverImageUrl = project.attachments?.find(
    attachment => attachment.mimeType?.startsWith('image/') && attachment.urls?.previewThumbnailUrl
  )?.urls?.previewThumbnailUrl;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${styles.card} ${isDragging ? styles.dragging : ''}`}
      onClick={() => navigate(`/asset/${assetId}/project-and-works/${project.id}`)}
    >
      {coverImageUrl && (
        <div className={styles.cover}>
          <img src={coverImageUrl} alt="" loading="lazy" className={styles.coverImage} />
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.tags}>
          {labelColor && (
            <div className={styles.labelDot} style={{ backgroundColor: labelColor.color }} />
          )}
          {project.priority && (
            <div
              className={styles.tag}
              style={{
                backgroundColor: PRIORITY_CONFIG[project.priority].bg,
                color: PRIORITY_CONFIG[project.priority].text,
              }}
            >
              {t(
                `projects.priority.${project.priority.toLowerCase()}`,
                PRIORITY_CONFIG[project.priority].label
              )}
            </div>
          )}
          {project.status === CreateProjectDtoStatusEnum.ProjectInProgress && (
            <div className={styles.tag} style={{ backgroundColor: '#E7F5FF', color: '#1971C2' }}>
              En cours
            </div>
          )}
        </div>
      </div>

      <div className={styles.title}>{project.title}</div>

      {project.description && extractPlainText(project.description) && (
        <div className={styles.description}>{extractPlainText(project.description)}</div>
      )}

      <div className={styles.footer}>
        <Group gap="xs">
          {creator ? (
            <Group gap="xs">
              <UserAvatar user={creator} size="xs" />
              <Text size="xs" fw={500}>
                {creator.firstName} {creator.lastName}
              </Text>
            </Group>
          ) : null}
          <div className={styles.date}>
            <IconCalendar size={14} />
            {project.deadline ? dayjs(project.deadline).format('LL') : '-'}
          </div>
        </Group>

        {project.status === CreateProjectDtoStatusEnum.IdeaBox && <LikeButton project={project} />}
      </div>

      {project.status === CreateProjectDtoStatusEnum.IdeaToVote && (
        <ProjectVoting project={project} />
      )}
    </div>
  );
};

export default ProjectCard;
